import * as THREE from 'three';
import { mulberry32, type Rng } from './rand.ts';

/*
 * Shared procedural textures and cached materials for the painted-miniature look.
 * Textures are generated into DataTextures from a seeded PRNG (no DOM needed, stable between reloads).
 * Material factories are cached by (kind, color, options): identical calls return the same instance,
 * which is what lets the engine merge and instance geometry by material.
 */

type Paint = (px: Uint8Array, size: number, rng: Rng) => void;

const makeTex = (size: number, seed: number, paint: Paint, srgb: boolean): THREE.DataTexture => {
  const px = new Uint8Array(size * size * 4);
  paint(px, size, mulberry32(seed));
  const t = new THREE.DataTexture(px, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
};

const fillRect = (px: Uint8Array, size: number, x: number, y: number, w: number, h: number, r: number, g: number, b: number): void => {
  const x0 = Math.floor(x), y0 = Math.floor(y);
  for (let j = y0; j < y0 + h; j++) {
    if (j < 0 || j >= size) continue;
    for (let i = x0; i < x0 + w; i++) {
      if (i < 0 || i >= size) continue;
      const o = (j * size + i) * 4;
      px[o] = r; px[o + 1] = g; px[o + 2] = b; px[o + 3] = 255;
    }
  }
};

const hex = (c: string): [number, number, number] => {
  const n = parseInt(c.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

let noise: THREE.DataTexture | undefined;
let flock: THREE.DataTexture | undefined;
let glow: THREE.DataTexture | undefined;

/** Grey speckle used as a bump map on every painted surface. */
export const noiseTex = (): THREE.DataTexture =>
  (noise ??= makeTex(256, 1, (px, s, rng) => {
    fillRect(px, s, 0, 0, s, s, 128, 128, 128);
    for (let i = 0; i < 5000; i++) {
      const v = (90 + rng() * 80) | 0;
      fillRect(px, s, rng() * s, rng() * s, 2, 2, v, v, v);
    }
  }, false));

/** Static-grass flock for the top of the display base. */
export const flockTex = (): THREE.DataTexture =>
  (flock ??= makeTex(256, 2, (px, s, rng) => {
    const [r, g, b] = hex('#3b3a24');
    fillRect(px, s, 0, 0, s, s, r, g, b);
    const cols = ['#55552f', '#2d2c1b', '#6b5a3a', '#4a5a2a', '#76683f'].map(hex);
    for (let i = 0; i < 6000; i++) {
      const c = cols[i % 5]!;
      const w = 2 + Math.floor(rng() * 2), h = 2 + Math.floor(rng() * 2);
      fillRect(px, s, rng() * s, rng() * s, w, h, c[0], c[1], c[2]);
    }
  }, true));

/** Soft white radial falloff for additive glow sprites. */
export const glowTex = (): THREE.DataTexture =>
  (glow ??= makeTex(128, 3, (px, s) => {
    const c = s / 2;
    for (let j = 0; j < s; j++) for (let i = 0; i < s; i++) {
      const d = Math.hypot(i + 0.5 - c, j + 0.5 - c) / c;
      const a = d < 0.25 ? 1 - (d / 0.25) * 0.45 : d < 1 ? 0.55 * (1 - (d - 0.25) / 0.75) : 0;
      const o = (j * s + i) * 4;
      px[o] = px[o + 1] = px[o + 2] = 255;
      px[o + 3] = Math.round(a * 255);
    }
  }, false));

/**
 * three r152+ normalizes the bump-map screen derivatives, so the reference (r128) bumpScale values
 * have to be scaled up to produce a similar amount of surface texture at gallery/game zoom.
 */
export const BUMP_K = 20;

export interface MatOpts {
  roughness?: number;
  metalness?: number;
  flatShading?: boolean;
  bumpScale?: number;
  side?: THREE.Side;
  transparent?: boolean;
  opacity?: number;
  depthWrite?: boolean;
  emissive?: string;
  emissiveIntensity?: number;
}

const cache = new Map<string, THREE.Material>();
const keyOf = (kind: string, color: string, o?: object): string => {
  if (!o) return `${kind}|${color}`;
  const e = Object.entries(o).filter(([, v]) => v !== undefined).sort(([a], [b]) => (a < b ? -1 : 1));
  return `${kind}|${color}|${e.map(([k, v]) => `${k}=${String(v)}`).join(',')}`;
};

/** Generic cache helper for materials that don't fit a factory below. */
export const cached = <T extends THREE.Material>(key: string, make: () => T): T => {
  const hit = cache.get(key);
  if (hit) return hit as T;
  const m = make();
  cache.set(key, m);
  return m;
};

/* ---------- night / glow registry ---------- */

const glowing = new Set<THREE.MeshStandardMaterial>();
const glowSprites = new Set<THREE.SpriteMaterial>();
let nightFactor = 0;

const registerGlow = (m: THREE.MeshStandardMaterial, base: number): void => {
  m.userData.base = base;
  m.emissiveIntensity = base * (1 + 1.6 * nightFactor);
  glowing.add(m);
};

/** A copy of a self-lit material (its skinned twin) brightens at night like the original. */
export const adoptGlow = (m: THREE.Material): void => {
  const s = m as THREE.MeshStandardMaterial;
  if (s.isMeshStandardMaterial && typeof s.userData.base === 'number') registerGlow(s, s.userData.base as number);
};

/** Moonlight: 0 = torchlight, 1 = full night. Boosts every emissive material and static glow sprite. */
export const setNight = (n: number): void => {
  nightFactor = n;
  for (const m of glowing) m.emissiveIntensity = (m.userData.base as number) * (1 + 1.6 * n);
  for (const s of glowSprites) s.opacity = Math.min(1, (s.userData.base as number) * (1 + 0.6 * n));
};

/* ---------- factories ---------- */

export const std = (color: string, o?: MatOpts): THREE.MeshStandardMaterial =>
  cached(keyOf('std', color, o), () => {
    const p: THREE.MeshStandardMaterialParameters = {
      color, roughness: 0.75, metalness: 0, bumpMap: noiseTex(), bumpScale: 0.012 * BUMP_K,
    };
    if (o) {
      const { bumpScale, emissive, ...rest } = o;
      Object.assign(p, rest);
      if (bumpScale !== undefined) p.bumpScale = bumpScale * BUMP_K;
      if (bumpScale === 0) p.bumpMap = null;
      if (emissive !== undefined) p.emissive = emissive;
    }
    const m = new THREE.MeshStandardMaterial(p);
    if (o?.emissive !== undefined) registerGlow(m, o.emissiveIntensity ?? 1);
    return m;
  });

export const metal = (c: string, o?: MatOpts): THREE.MeshStandardMaterial => std(c, { metalness: 0.8, roughness: 0.35, ...o });
export const cloth = (c: string, o?: MatOpts): THREE.MeshStandardMaterial => std(c, { roughness: 0.95, bumpScale: 0.02, ...o });
export const leather = (c: string, o?: MatOpts): THREE.MeshStandardMaterial => std(c, { roughness: 0.6, ...o });
export const stone = (c: string, o?: MatOpts): THREE.MeshStandardMaterial => std(c, { roughness: 0.95, flatShading: true, bumpScale: 0.03, ...o });

/** Self-lit material (runes, embers, crystals). `b` is the torchlight emissive intensity. */
export const emis = (color: string, e: string, b: number, o?: MatOpts): THREE.MeshStandardMaterial =>
  std(color, { ...o, emissive: e, emissiveIntensity: b });

/** Translucent ghost material (sleeping player's spectre). */
export const spectral = (color: string): THREE.MeshStandardMaterial =>
  cached(keyOf('spectral', color), () => {
    const m = new THREE.MeshStandardMaterial({
      color, emissive: color, emissiveIntensity: 0.35, transparent: true, opacity: 0.5, depthWrite: false, roughness: 0.6,
    });
    registerGlow(m, 0.35);
    return m;
  });

/** Translucent smoky body without bump (shadow nightmares). */
export const smoke = (color: string, opacity: number, o?: MatOpts): THREE.MeshStandardMaterial =>
  cached(keyOf('smoke', color, { opacity, ...o }), () =>
    new THREE.MeshStandardMaterial({ color, transparent: true, opacity, depthWrite: false, roughness: 1, ...o, emissive: o?.emissive ?? '#000000' }));

/** Unlit translucent wisps. */
export const basic = (color: string, opacity: number): THREE.MeshBasicMaterial =>
  cached(keyOf('basic', color, { opacity }), () => new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false }));

/** Additive glow sprite material. Shared ones get the night boost; `unique` ones are animated by their model. */
export const glowMat = (color: string, opacity: number, unique = false): THREE.SpriteMaterial => {
  const make = (): THREE.SpriteMaterial => {
    const m = new THREE.SpriteMaterial({
      map: glowTex(), color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity,
    });
    m.userData.base = opacity;
    return m;
  };
  if (unique) return make();
  return cached(keyOf('glow', color, { opacity }), () => {
    const m = make();
    m.opacity = Math.min(1, opacity * (1 + 0.6 * nightFactor));
    glowSprites.add(m);
    return m;
  });
};

/* ---------- named palette ---------- */

export const STEEL = metal('#a7aeb6');
export const DSTEEL = metal('#4a4f57');
export const GOLD = metal('#c9a24a', { roughness: 0.3 });
export const BRASS = metal('#b08a3e', { roughness: 0.4 });
export const BRONZE = metal('#8c5a2b', { roughness: 0.45 });
export const WOOD = std('#5b3d26', { roughness: 0.85 });
export const DWOOD = std('#3a271a', { roughness: 0.9 });
export const SKIN = std('#c99a7a', { roughness: 0.7 });
export const BONE = std('#d8ccb0', { roughness: 0.8 });
export const BLACK = std('#0c0a0c', { roughness: 0.5 });
export const LEATHER_BELT = leather('#3b2716');
/** Bowstrings, ropes. */
export const STRING = std('#d8d0bd');
